import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEOHead = ({ 
  title,
  description,
  keywords,
  canonical,
  image,
  type = 'website',
  article = null
}) => {
  const siteUrl = 'https://validatedboat.fr';
  const siteName = 'validatedboat';
  
  // Default values
  const defaultTitle = 'ValidatedBoat — Conseiller nautique indépendant | Analyse d\'annonce, audit pré-achat & accompagnement | La Rochelle';
  const defaultDescription = "Vous avez repéré un bateau d'occasion ? ValidatedBoat, conseiller nautique indépendant à La Rochelle, analyse l'annonce, vérifie la cohérence du prix, identifie les points de vigilance et vous accompagne jusqu'à l'achat.";
  const defaultKeywords = "analyse annonce bateau, conseiller nautique La Rochelle, audit pré-achat bateau, achat bateau d'occasion La Rochelle, aide achat voilier, négociation bateau d'occasion, accompagnement achat bateau, estimation bateau La Rochelle";
  const defaultImage = 'https://images.pexels.com/photos/1295036/pexels-photo-1295036.jpeg?auto=compress&cs=tinysrgb&w=1200';

  // Use provided values or defaults
  const pageTitle = title || defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageKeywords = keywords || defaultKeywords;
  const pageImage = image || defaultImage;
  const pageCanonical = canonical || siteUrl;

  // Schema.org structured data for organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "validatedboat",
    "description": defaultDescription,
    "url": siteUrl,
    "email": "contact@validatedboat.fr",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "La Rochelle",
      "addressRegion": "Nouvelle-Aquitaine",
      "postalCode": "17000",
      "addressCountry": "FR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 46.1591,
      "longitude": -1.1520
    },
    "areaServed": {
      "@type": "Country",
      "name": "France"
    },
    "priceRange": "€€",
    "serviceType": [
      "Analyse d'annonce de bateau",
      "Conseiller nautique indépendant",
      "Audit pré-achat bateau",
      "Recherche bateau sur mesure",
      "Négociation bateau d'occasion",
      "Estimation bateau"
    ],
    "slogan": "Conseiller nautique indépendant — Analyse d'annonce, audit et accompagnement acheteur"
  };

  // Article schema for blog posts
  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image,
    "datePublished": article.publishedDate,
    "dateModified": article.publishedDate,
    "author": {
      "@type": "Organization",
      "name": "validatedboat"
    },
    "publisher": {
      "@type": "Organization",
      "name": "validatedboat",
      "url": siteUrl
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": pageCanonical
    }
  } : null;

  // BreadcrumbList schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": siteUrl
      }
    ]
  };

  // Add article to breadcrumb if it's a blog post
  if (article) {
    breadcrumbSchema.itemListElement.push(
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": `${siteUrl}/blog`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.title,
        "item": pageCanonical
      }
    );
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang="fr" />
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="author" content="validatedboat" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="language" content="fr" />
      <meta name="revisit-after" content="7 days" />
      <link rel="canonical" href={pageCanonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'article' ? 'article' : 'website'} />
      <meta property="og:url" content={pageCanonical} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="fr_FR" />

      {/* Article specific Open Graph */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedDate} />
          <meta property="article:modified_time" content={article.publishedDate} />
          <meta property="article:author" content="validatedboat" />
          <meta property="article:section" content={article.category} />
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageCanonical} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Additional SEO Tags */}
      <meta name="theme-color" content="#1a1c1b" />
      <meta name="msapplication-TileColor" content="#1a1c1b" />
      
      {/* Geo Tags */}
      <meta name="geo.region" content="FR-17" />
      <meta name="geo.placename" content="La Rochelle" />
      <meta name="geo.position" content="46.1591;-1.1520" />
      <meta name="ICBM" content="46.1591, -1.1520" />

      {/* Schema.org Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>

      {/* Article Schema */}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
